import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiFailure,
  type Branding,
  type Plan,
  type Schedule,
  type Share,
  type WorkspaceDetail,
} from '../api';
import { Notice } from './Panels';
import { Field, Hint } from './Hint';

/**
 * Workspace settings: plan and usage, branding, people, sharing, schedules.
 *
 * One page rather than five, because these are all "things an owner configures
 * once" and splitting them across a settings tree makes each one harder to
 * find than it is to change.
 */
export function Settings({
  onPlanChanged,
  reportId,
  datasetId,
}: {
  onPlanChanged: () => void;
  reportId: string | null;
  datasetId: string | null;
}) {
  const [detail, setDetail] = useState<WorkspaceDetail | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(() => {
    api
      .workspace()
      .then(setDetail)
      .catch((error: unknown) => {
        if (error instanceof ApiFailure) setFailure(error);
      });
  }, []);

  useEffect(reload, [reload]);

  if (detail === null) {
    return (
      <div className="page">
        {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const canAdminister = detail.workspace.role === 'owner' || detail.workspace.role === 'admin';

  return (
    <div className="page">
      <header className="intro">
        <h2>
          Workspace
          <Hint text="A workspace is your team's shared home: one set of data, reports and people. Everyone in it sees the same thing." />
        </h2>
        <p>
          {detail.workspace.name} · you are {detail.workspace.role === 'owner' ? 'the owner' : `a ${detail.workspace.role}`}.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
      {notice !== null && <Notice tone="good" message={notice} />}

      <PlanCard
        detail={detail}
        canAdminister={canAdminister}
        onChanged={() => {
          reload();
          onPlanChanged();
        }}
        onFailure={setFailure}
      />

      <BrandingCard
        detail={detail}
        canAdminister={canAdminister}
        onSaved={(message) => {
          setNotice(message);
          reload();
        }}
        onFailure={setFailure}
      />

      <SharesCard reportId={reportId} onFailure={setFailure} />

      <SchedulesCard datasetId={datasetId} onFailure={setFailure} />

      <MembersCard detail={detail} canAdminister={canAdminister} onChanged={reload} onFailure={setFailure} />
    </div>
  );
}

// -----------------------------------------------------------------------------

function PlanCard({
  detail,
  canAdminister,
  onChanged,
  onFailure,
}: {
  detail: WorkspaceDetail;
  canAdminister: boolean;
  onChanged: () => void;
  onFailure: (failure: ApiFailure) => void;
}) {
  const [busy, setBusy] = useState(false);
  const { usage, limits, workspace } = detail;

  const bars: { label: string; used: number; allowed: number }[] = [
    { label: 'Datasets', used: usage.datasetsCreated, allowed: limits.datasetsPerMonth },
    { label: 'Rows', used: usage.rowsIngested, allowed: limits.rowsPerMonth },
    { label: 'AI summaries', used: usage.narrationsRun, allowed: limits.narrationsPerMonth },
  ];

  async function change(plan: Plan): Promise<void> {
    setBusy(true);
    try {
      await api.setPlan(plan);
      onChanged();
    } catch (error) {
      if (error instanceof ApiFailure) onFailure(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <header>
        <h2>
          Plan and usage
          <Hint text="Your plan sets how much you can use each month. The bars show how much of this month's allowance is used; it restarts on the 1st." />
        </h2>
        <span className="sub">this calendar month · resets on the 1st</span>
      </header>

      <div className="usage-bars">
        {bars.map((bar) => {
          const fraction = bar.allowed === 0 ? 0 : Math.min(1, bar.used / bar.allowed);
          return (
            <div key={bar.label}>
              <div className="usage-label">
                <span>{bar.label}</span>
                <span>
                  {bar.used.toLocaleString('en-US')} / {bar.allowed.toLocaleString('en-US')}
                </span>
              </div>
              <div className="meter">
                {/* Amber past 80% so a limit is visible before it is hit. */}
                <div
                  style={{
                    width: `${Math.round(fraction * 100)}%`,
                    background: fraction >= 1 ? 'var(--critical)' : fraction > 0.8 ? 'var(--warning)' : 'var(--series-1)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="plan-grid">
        {(['free', 'pro', 'agency'] as const).map((plan) => (
          <div key={plan} className={plan === workspace.plan ? 'plan current' : 'plan'}>
            <div className="plan-name">{plan}</div>
            <ul>
              <li>{plan === 'free' ? '10' : plan === 'pro' ? '200' : '2,000'} datasets / month</li>
              <li>{plan === 'free' ? 'No' : plan === 'pro' ? '10' : '100'} scheduled refreshes</li>
              <li>{plan === 'agency' ? 'White-label exports' : 'DataVantage branding'}</li>
              <li>{plan === 'free' ? '1' : plan === 'pro' ? '5' : '50'} members</li>
            </ul>
            {plan === workspace.plan ? (
              <span className="badge good">current</span>
            ) : (
              canAdminister && (
                <button className="ghost tiny" disabled={busy} onClick={() => void change(plan)}>
                  Switch to {plan}
                </button>
              )
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function BrandingCard({
  detail,
  canAdminister,
  onSaved,
  onFailure,
}: {
  detail: WorkspaceDetail;
  canAdminister: boolean;
  onSaved: (message: string) => void;
  onFailure: (failure: ApiFailure) => void;
}) {
  const [branding, setBranding] = useState<Branding>(detail.workspace.branding);
  const [busy, setBusy] = useState(false);
  const locked = !detail.limits.whiteLabel;

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await api.updateWorkspace({ branding });
      onSaved('Branding saved. It applies to the next export you generate.');
    } catch (error) {
      if (error instanceof ApiFailure) onFailure(error);
    } finally {
      setBusy(false);
    }
  }

  function readLogo(file: File): void {
    // Read to a data URI so the logo travels with the PDF. A remote URL would
    // make every export fetch an arbitrary host at render time.
    const reader = new FileReader();
    reader.onload = () => setBranding({ ...branding, logoUrl: String(reader.result) });
    reader.readAsDataURL(file);
  }

  return (
    <section className="card">
      <header>
        <h2>Branding</h2>
        <span className="sub">{locked ? 'agency plan' : 'applies to PDF exports and digests'}</span>
      </header>

      <div className="row" style={{ alignItems: 'flex-start', gap: 20 }}>
        <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Company name" hint="Your company name replaces “DataVantage” on PDF reports and email digests.">
            <input
              type="text"
              value={branding.companyName ?? ''}
              placeholder="Shown instead of DataVantage"
              disabled={!canAdminister}
              onChange={(e) => setBranding({ ...branding, companyName: e.target.value })}
            />
          </Field>

          <Field label="Accent colour" hint="The colour used for headings and highlights on your exported reports.">
            <input
              type="color"
              value={branding.accentColor ?? '#2a78d6'}
              disabled={!canAdminister}
              onChange={(e) => setBranding({ ...branding, accentColor: e.target.value })}
              style={{ width: 64, height: 32, padding: 2 }}
            />
          </Field>

          <Field label="Footer note" hint="A short line printed at the bottom of each export, for example who the report was prepared for.">
            <input
              type="text"
              value={branding.footerNote ?? ''}
              placeholder="Prepared for…"
              disabled={!canAdminister}
              onChange={(e) => setBranding({ ...branding, footerNote: e.target.value })}
            />
          </Field>

          <label className="field">
            Logo
            <input
              type="file"
              accept="image/png,image/jpeg"
              disabled={!canAdminister || locked}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file !== undefined) readLogo(file);
              }}
            />
            <span className="field-help">PNG or JPEG. Embedded in the PDF, so it works offline.</span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={!branding.showPoweredBy}
              disabled={!canAdminister || locked}
              onChange={(e) => setBranding({ ...branding, showPoweredBy: !e.target.checked })}
            />
            Remove “Generated by DataVantage” from exports
            <Hint text="Hides the DataVantage credit on your exports. Available on the agency plan." />
          </label>

          {locked && (
            <Notice
              message="Logo and attribution removal are agency-plan features."
              remedy="A company name, accent colour and footer note work on any plan."
            />
          )}

          {canAdminister && (
            <button className="primary" disabled={busy} onClick={() => void save()} style={{ alignSelf: 'flex-start' }}>
              {busy ? <span className="spinner" /> : 'Save branding'}
            </button>
          )}
        </div>

        {/* A live preview of the export cover, so the effect is visible before
            generating a PDF to check it. */}
        <div className="brand-preview" style={{ borderTopColor: branding.accentColor ?? '#2a78d6' }}>
          {branding.logoUrl !== null && branding.logoUrl !== '' && (
            <img src={branding.logoUrl} alt="" />
          )}
          <div className="eyebrow" style={{ color: branding.accentColor ?? '#2a78d6' }}>
            {(branding.companyName ?? 'DataVantage').toUpperCase()}
          </div>
          <div className="title">sales.csv</div>
          <div className="meta">12 rows · 6 columns · 100.0% complete</div>
          {/* The preview needs a title and figures to show the layout; these
              are sample values, labelled so they are not mistaken for a
              computed result. */}
          <div className="meta" style={{ fontStyle: 'italic' }}>Sample data — preview only</div>
          {branding.footerNote !== null && branding.footerNote !== '' && (
            <div className="meta">{branding.footerNote}</div>
          )}
          <div className="foot">
            {branding.showPoweredBy ? 'Generated by DataVantage' : ' '}
          </div>
        </div>
      </div>
    </section>
  );
}

function SharesCard({
  reportId,
  onFailure,
}: {
  reportId: string | null;
  onFailure: (failure: ApiFailure) => void;
}) {
  const [shares, setShares] = useState<Share[]>([]);
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api
      .listShares()
      .then((r) => setShares(r.shares))
      .catch(() => setShares([]));
  }, []);

  useEffect(reload, [reload]);

  async function create(): Promise<void> {
    if (reportId === null) return;
    setBusy(true);
    try {
      const result = await api.createShare({ reportId });
      // Shown once — only the hash is stored, so it cannot be retrieved later.
      setCreated(result.url);
      reload();
    } catch (error) {
      if (error instanceof ApiFailure) onFailure(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <header>
        <h2>
          Share links
          <Hint text="A link lets anyone view one report in their browser, no account needed. You can cancel a link at any time." />
        </h2>
        <span className="sub">read-only, no account needed</span>
      </header>

      {reportId === null ? (
        <p className="muted">Open a report first, then create a link to it.</p>
      ) : (
        <button className="primary" disabled={busy} onClick={() => void create()}>
          {busy ? <span className="spinner" /> : 'Create a link to the open report'}
        </button>
      )}

      {created !== null && (
        <div style={{ marginTop: 12 }}>
          <Notice
            tone="good"
            message="Link created — copy it now."
            remedy="Only its hash is stored, so this is the one time it can be shown."
          />
          <div className="row" style={{ marginTop: 8 }}>
            <input type="text" readOnly value={created} style={{ flex: 1, minWidth: 260 }} />
            <button onClick={() => void navigator.clipboard.writeText(created)}>Copy</button>
          </div>
        </div>
      )}

      {shares.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table>
            <thead>
              <tr>
                <th>Created</th>
                <th className="num">Views</th>
                <th>Expires</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shares.map((share) => (
                <tr key={share.id}>
                  <td>{new Date(share.createdAt).toLocaleDateString()}</td>
                  <td className="num">{share.viewCount}</td>
                  <td>{share.expiresAt === null ? 'never' : new Date(share.expiresAt).toLocaleDateString()}</td>
                  <td>
                    {share.revoked ? (
                      <span className="badge critical">revoked</span>
                    ) : (
                      <span className="badge good">active</span>
                    )}
                  </td>
                  <td>
                    {!share.revoked && (
                      <button
                        className="ghost tiny danger"
                        onClick={async () => {
                          await api.revokeShare(share.id);
                          reload();
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SchedulesCard({
  datasetId,
  onFailure,
}: {
  datasetId: string | null;
  onFailure: (failure: ApiFailure) => void;
}) {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [provider, setProvider] = useState('console');
  const [running, setRunning] = useState<string | null>(null);

  const reload = useCallback(() => {
    api
      .listSchedules()
      .then((r) => {
        setSchedules(r.schedules);
        setEnabled(r.enabled);
        setProvider(r.emailProvider);
      })
      .catch(() => setSchedules([]));
  }, []);

  useEffect(reload, [reload]);

  return (
    <section className="card">
      <header>
        <h2>
          Scheduled refresh
          <Hint text="Checks your connected data on a regular schedule and emails you only when the numbers change." />
        </h2>
        <span className="sub">{schedules.length} configured</span>
      </header>

      {!enabled && (
        <Notice
          message="This deployment cannot store credentials, so scheduling is off."
          remedy="Set CREDENTIAL_ENCRYPTION_KEY in the server environment to enable it."
        />
      )}

      {provider === 'console' && enabled && (
        <Notice
          message="No email provider is configured — digests will be logged, not sent."
          remedy="Set EMAIL_PROVIDER, EMAIL_API_KEY and EMAIL_FROM to deliver them."
        />
      )}

      {schedules.length === 0 ? (
        <p className="muted" style={{ marginTop: 10 }}>
          A schedule re-fetches a connector-backed dataset and emails you only when something moved.
          Create one from the Sources page after connecting a platform
          {datasetId === null ? '' : ', or against the open dataset'}.
        </p>
      ) : (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Cadence</th>
                <th>Next run</th>
                <th>Last run</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule) => (
                <tr key={schedule.id}>
                  <td>{schedule.name}</td>
                  <td>
                    {schedule.cadence} at {String(schedule.hourUtc).padStart(2, '0')}:00 UTC
                  </td>
                  <td>{new Date(schedule.nextRunAt).toLocaleString()}</td>
                  <td>
                    {schedule.lastRunStatus === null ? (
                      <span className="muted">never</span>
                    ) : schedule.lastRunStatus === 'ok' ? (
                      <span className="badge good">ok</span>
                    ) : (
                      <span className="badge critical" title={schedule.lastRunError ?? ''}>
                        failed
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="row tight">
                      <button
                        className="ghost tiny"
                        disabled={running !== null}
                        onClick={async () => {
                          setRunning(schedule.id);
                          try {
                            await api.runSchedule(schedule.id);
                            reload();
                          } catch (error) {
                            if (error instanceof ApiFailure) onFailure(error);
                          } finally {
                            setRunning(null);
                          }
                        }}
                      >
                        {running === schedule.id ? <span className="spinner" /> : 'Run now'}
                      </button>
                      <button
                        className="ghost tiny"
                        onClick={async () => {
                          await api.updateSchedule(schedule.id, { active: !schedule.active });
                          reload();
                        }}
                      >
                        {schedule.active ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        className="ghost tiny danger"
                        onClick={async () => {
                          await api.deleteSchedule(schedule.id);
                          reload();
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {schedules.some((s) => s.lastRunStatus === 'failed') && (
        <div style={{ marginTop: 10 }}>
          <Notice
            message="A schedule failed on its last run."
            remedy={schedules.find((s) => s.lastRunStatus === 'failed')?.lastRunError ?? ''}
          />
        </div>
      )}
    </section>
  );
}

function MembersCard({
  detail,
  canAdminister,
  onChanged,
  onFailure,
}: {
  detail: WorkspaceDetail;
  canAdminister: boolean;
  onChanged: () => void;
  onFailure: (failure: ApiFailure) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [busy, setBusy] = useState(false);

  return (
    <section className="card">
      <header>
        <h2>People</h2>
        <span className="sub">
          {detail.members.length} of {detail.limits.members}
        </span>
      </header>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {detail.members.map((member) => (
              <tr key={member.userId}>
                <td>{member.email}</td>
                <td>{member.name}</td>
                <td>
                  <span className="badge">{member.role}</span>
                </td>
                <td>
                  {canAdminister && member.role !== 'owner' && (
                    <button
                      className="ghost tiny danger"
                      onClick={async () => {
                        try {
                          await api.removeMember(member.userId);
                          onChanged();
                        } catch (error) {
                          if (error instanceof ApiFailure) onFailure(error);
                        }
                      }}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canAdminister && (
        <div className="row" style={{ marginTop: 12 }}>
          <Field
            label="Add an existing account"
            hint="They need to have signed up already; they'll see this workspace as soon as they're added."
            className="grow"
          >
            <input
              type="email"
              value={email}
              placeholder="colleague@company.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Role" hint="Admins manage people, billing and settings. Members can edit reports. Viewers can only look.">
            <select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
              <option value="admin">admin</option>
              <option value="member">member</option>
              <option value="viewer">viewer — read only</option>
            </select>
          </Field>
          <button
            disabled={busy || email === ''}
            style={{ alignSelf: 'flex-end' }}
            onClick={async () => {
              setBusy(true);
              try {
                await api.addMember({ email, role });
                setEmail('');
                onChanged();
              } catch (error) {
                if (error instanceof ApiFailure) onFailure(error);
              } finally {
                setBusy(false);
              }
            }}
          >
            Add
          </button>
        </div>
      )}
    </section>
  );
}
